import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import portfolio from "@/data/portfolio";

export default function NationalLeague() {
  const d = portfolio.league;
  return (
    <section
      id="league"
      data-testid="national-league-section"
      className="py-20 md:py-28 border-t hairline bg-[#FAFAFA]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-center mb-12 md:mb-16">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-block w-8 h-px bg-black/40" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
                03 · {d.eyebrow}
              </span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] font-light tracking-tight text-black leading-[1.05]">
              {d.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-gray-700 font-light leading-relaxed max-w-2xl">
              {d.headline}
            </p>
          </div>

          <div className="lg:col-span-4 flex justify-center lg:justify-end">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-44 sm:w-52 md:w-56 lg:w-60 aspect-square"
            >
              <img
                src={d.image}
                alt="National Equestrian League"
                className={`w-full h-full ${
                  d.imageFit === "contain" ? "object-contain" : "object-cover rounded-3xl"
                }`}
              />
            </motion.div>
          </div>
        </div>

        {/* Pillars laid out as a startup case study */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          {d.pillars.map((p, i) => (
            <motion.div
              key={p}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.04 }}
              data-testid={`league-pillar-${i}`}
              className="bg-white border hairline rounded-2xl p-5 md:p-7 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/5 transition-all duration-400"
            >
              <div className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-2 md:mt-3 font-serif-display text-lg md:text-2xl text-black tracking-tight leading-tight">
                {p}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 border-t hairline">
          {d.metrics.map((m, i) => (
            <div
              key={m.label}
              data-testid={`league-metric-${i}`}
              className={`py-8 md:py-10 ${i > 0 ? "md:border-l hairline" : ""} ${i >= 2 ? "border-t md:border-t-0 hairline" : ""} ${(i === 1 || i === 3) ? "border-l hairline" : ""} pl-5 md:pl-6`}
            >
              <div className="font-serif-display text-4xl md:text-5xl font-light text-black tracking-tight">
                {m.value}
              </div>
              <div className="mt-3 text-sm text-gray-500">{m.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 flex justify-start">
          <Link
            to="/projects/national-equestrian-league"
            data-testid="league-open-case-study"
            className="inline-flex items-center gap-2 bg-black text-white hover:bg-[#1a1a1a] rounded-full px-6 py-3 text-sm font-medium transition-colors"
          >
            View more <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
